import { IS_LOCAL } from '@/helpers';

const I18N = {
  ko: {
    dailyMissionDetail: {
      dateFormat: 'yy.LL.dd',
    },
  },
  en: {
    dailyMissionDetail: {
      dateFormat: 'LLL dd, yyyy',
    },
  },
};

const EnvironmentStore = {
  namespaced: true,
  state() {
    return {
      /** 데일리 미션 상태 */
      dailyMissionStatus: {
        attendance: 1,
        invite: 0,
        subs: 0,
        rentalIncome: 0,
        swap: 0,
      },
      /** 데일리 미션 상세 */
      dailyMissionDetail: [],
      /** 획득 재료 내역 */
      stickerStatus: {
        unused: 0,
        total: 0,
      },
      /** 나의 완성 스티커 세트 상태 */
      myCompletedStickerSetStatus: {
        usrCompleteStickerSets: 0,
        receivedStickerSetsRewards: [],
      },
    };
  },
  getters: {
    getDailyMissionDetailSum: (state) => state.dailyMissionDetail.reduce((acc, item) => acc + item.value, 0),
  },
  mutations: {
    setDailyMissionStatus(state, status) {
      state.dailyMissionStatus = status;
    },
    setDailyMissionDetail(state, detail) {
      state.dailyMissionDetail = detail;
    },
    setStickerStatus(state, status) {
      state.stickerStatus = status;
    },
    setMyCompletedStickerSetStatus(state, status) {
      state.myCompletedStickerSetStatus = status;
    },
  },
  actions: {
    async fetch(_, { method, url, params }) {
      const data = method === 'get' ? { params: { ...params } } : params;
      const response = await this._vm.$axios[method](`${process.env.VUE_APP_TEST_SERVER}${url}`, data);
      return response;
    },
    /**
     * 데일리 미션 상태 조회
     * @returns {Promise<{
     *   invite: number;
     *   subs: number;
     *   rentalIncome: number;
     *   swap: number;
     * }>}
     */
    async getDailyMissionStatus({ dispatch }) {
      if (IS_LOCAL) {
        return {
          data: {
            invite: 4,
            subs: 2,
            rentalIncome: 1,
            swap: 1,
          },
        };
      }

      const response = await dispatch('fetch', {
        method: 'get',
        url: '/environment/mission',
      });
      return response;
    },
    /**
     * 데일리 미션 상태 조회
     */
    async fetchDailyMissionStatus({ dispatch, commit }) {
      const { data: dailyMissionStatus } = await dispatch('getDailyMissionStatus');

      commit('setDailyMissionStatus', dailyMissionStatus);

      return;
    },
    /**
     * 데일리 미션 상세 조회
     * @returns {Promise<{
     *   date: string;
     *   content: string;
     *   value: number;
     * }[]>}
     */
    async getDailyMissionDetail({ dispatch }) {
      if (IS_LOCAL) {
        return {
          data: [
            {
              date: '2023-01-01T00:00:00',
              content: '출석 체크 완료',
              value: 1,
            },
            {
              date: '2023-01-02T00:00:00',
              content: '출석 체크 완료',
              value: 4,
            },
          ],
        };
      }

      const response = await dispatch('fetch', {
        method: 'get',
        url: '/environment/mission/detail',
      });
      return response;
    },
    /**
     * 데일리 미션 상세 조회
     */
    async fetchDailyMissionDetail({ dispatch, commit, rootState }) {
      const { data: dailyMissionDetail } = await dispatch('getDailyMissionDetail');
      const { lang } = rootState;

      const format = I18N[lang].dailyMissionDetail.dateFormat;
      const formattedDailyMissionDetail = dailyMissionDetail.map((item) => ({
        ...item,
        date: this._vm.$tl(item.date, lang).toFormat(format),
      }));

      commit('setDailyMissionDetail', formattedDailyMissionDetail);
      return;
    },
    /**
     * @description 획득 스티커 내역 조회
     */
    async getStickerStatus({ dispatch }) {
      if (IS_LOCAL) {
        return {
          data: {
            unused: 5,
            total: 9,
          },
        };
      }

      const response = await dispatch('fetch', {
        method: 'get',
        url: '/environment/opportunity',
      });
      return response;
    },
    /** 획득 스티커 내역 조회 */
    async fetchStickerStatus({ dispatch, commit }) {
      const { data: stickerStatus } = await dispatch('getStickerStatus');
      console.log(stickerStatus);
      commit('setStickerStatus', stickerStatus);

      return;
    },
    /**
     * @description 나의 완성 스티커세트 상태 조회
     */
    async getMyCompletedStickerSetStatus({ dispatch }) {
      if (IS_LOCAL) {
        return {
          data: {
            // 유저 완성 스티커 세트 개수 0 ~ n
            usrCompleteStickerSets: 2,
            // 유저가 수령한 보상
            receivedStickerSetsRewards: [1, 3], // index 숫자
          },
        };
      }

      const response = await dispatch('fetch', {
        method: 'get',
        url: '/environment/reward/complete',
      });
      return response;
    },
    /** 나의 완성 스티커 세트 상태 조회 */
    async fetchMyCompletedStickerSetStatus({ dispatch, commit }) {
      const { data: myCompletedStickerSetStatus } = await dispatch('getMyCompletedStickerSetStatus');
      console.log(myCompletedStickerSetStatus);
      commit('setMyCompletedStickerSetStatus', myCompletedStickerSetStatus);

      return;
    },
    /**
     * @description 스티커 세트 완성 보상 받기
     * @returns {Promise<number>}
     * @example 1 // 0.5, 1, 2, 2.5, 5
     */
    async postStickerSetReward({ dispatch }) {
      if (IS_LOCAL) {
        return {
          data: 1,
        };
      }

      const response = await dispatch('fetch', {
        method: 'post',
        url: '/environment/draw',
      });
      return response;
    },
    /** 스티커 세트 완성 보상 받기 */
    async submitStickerSetReward({ dispatch }) {
      try {
        const { data } = await dispatch('postStickerSetReward');
        return data;
      } catch (error) {
        if (!error.response?.data?.message) return false;

        return error.response.data.message;
      } finally {
        dispatch('fetchStickerStatus');
        dispatch('fetchMyCompletedStickerSetStatus');
      }
    },
    /** 완성 스티커 세트 보상 받기 */
    async postCompletedStickerSetReward({ dispatch }, index) {
      if (IS_LOCAL) {
        return {
          omc: 1,
          lCoupon: 2,
        };
      }

      const response = await dispatch('fetch', {
        method: 'post',
        url: '/environment/reward/complete',
        params: {
          stickerSet: index,
        },
      });
      return response;
    },
    /** 완성 스티커 세트 보상 받기 */
    async submitCompletedStickerSetReward({ dispatch }, index) {
      try {
        const { data } = await dispatch('postCompletedStickerSetReward', index);

        return data;
      } catch (error) {
        if (!error.response?.data?.message) return false;

        return error.response.data.message;
      } finally {
        dispatch('fetchMyCompletedStickerSetStatus');
      }
    },
  },
};

export default EnvironmentStore;
